// /app/practice — start a Practice Test (BRIEFS C7.4, SPEC §5 item 4).
// Chapter multi-select (released chapters with their question counts), number of questions
// (quick picks 10/25/50/90 + a box, 5–200), Start. The server picks the questions (C7.3).
// "PYQs only" switch (C11.4): only questions that came in NEET / AIPMT; counts follow it.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOpenTest, getTestChapters, startPractice } from '../../api/v2';
import { track, useActivityArea } from '../../components/app/activity';
import { IconCheck } from '../../components/app/icons';
import { OpenTestBanner, errorText, plural } from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { Empty, LoadError, Loading } from '../../components/app/LoadState';

const QUICK = [10, 25, 50, 90];
const MIN = 5;
const MAX = 200;

const box = {
  boxSizing: 'border-box', padding: 20, background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 12,
};
const h2 = { fontFamily: FONT.heading, margin: 0, fontSize: 17, fontWeight: 600 };

export function chapterName(ch) {
  return `${ch.class_level} · Chapter ${ch.ncert_no} · ${ch.title}`;
}

function CheckRow({ checked, onClick, children, testId, disabled = false }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} onClick={onClick} disabled={disabled}
      data-testid={testId} style={{
        display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '8px 14px',
        boxSizing: 'border-box', width: '100%', borderRadius: 10, textAlign: 'left',
        border: `2px solid ${checked ? C.primary : C.border}`, background: checked ? C.tint : C.surface,
        fontFamily: FONT.ui, fontSize: 15, color: disabled ? C.muted : C.text,
        cursor: disabled ? 'default' : 'pointer',
      }}>
      <span aria-hidden="true" style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: '#FFFFFF',
        border: `2px solid ${checked ? C.primary : '#94A3B8'}`, background: checked ? C.primary : C.surface,
      }}>{checked && <IconCheck />}</span>
      {children}
    </button>
  );
}
export { CheckRow };

// "PYQs only" (C11.4): a switch row with how many PYQs there are. Off when there are none.
export function PyqSwitch({ on, onClick, count }) {
  const none = !count && !on;
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onClick} disabled={none}
      data-testid="v2-pyq-only" style={{
        display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '8px 14px',
        boxSizing: 'border-box', width: '100%', borderRadius: 10, textAlign: 'left',
        border: `2px solid ${on ? C.streak : C.border}`, background: on ? C.streakBg : C.surface,
        fontFamily: FONT.ui, fontSize: 15, color: none ? C.muted : C.text, cursor: none ? 'default' : 'pointer',
      }}>
      <span aria-hidden="true" style={{
        width: 40, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
        background: on ? C.streak : '#CBD5E1', transition: 'background 0.15s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 19 : 3, width: 18, height: 18, borderRadius: 9,
          background: '#FFFFFF', transition: 'left 0.15s',
        }} />
      </span>
      <span style={{ flexGrow: 1 }}>
        <span style={{ fontWeight: 600 }}>PYQs only</span>
        <span style={{ color: C.muted }}> — questions that came in NEET</span>
      </span>
      <span data-testid="v2-pyq-count" style={{ fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
        {none ? 'No PYQs yet' : plural(count, 'PYQ', 'PYQs')}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: on ? C.streak : C.muted, minWidth: 26 }}>{on ? 'On' : 'Off'}</span>
    </button>
  );
}

export default function AppPractice() {
  useActivityArea('test');
  const navigate = useNavigate();
  const [picked, setPicked] = useState([]);
  const [count, setCount] = useState(25);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pyqOnly, setPyqOnly] = useState(false);

  const chaptersQ = useQuery({ queryKey: ['v2', 'test-chapters'], queryFn: getTestChapters });
  const openQ = useQuery({ queryKey: ['v2', 'open-test'], queryFn: getOpenTest });
  const chapters = useMemo(() => (chaptersQ.data && chaptersQ.data.chapters) || [], [chaptersQ.data]);
  const countOf = (c) => (pyqOnly ? c.pyqs || 0 : c.questions || 0);
  const available = chapters.filter((c) => picked.includes(c.chapter_id))
    .reduce((n, c) => n + countOf(c), 0);
  const pyqAll = chapters.reduce((n, c) => n + (c.pyqs || 0), 0);
  const pyqPicked = chapters.filter((c) => picked.includes(c.chapter_id)).reduce((n, c) => n + (c.pyqs || 0), 0);
  const openTest = openQ.data && openQ.data.test;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const usable = chapters.filter((c) => countOf(c) > 0);
  const switchPyq = () => {
    const on = !pyqOnly;
    setPyqOnly(on);
    if (on) setPicked((p) => p.filter((id) => (chapters.find((c) => c.chapter_id === id) || {}).pyqs > 0));
  };
  const allPicked = usable.length > 0 && usable.every((c) => picked.includes(c.chapter_id));

  const typedNum = typed === '' ? null : Number(typed);
  const typedBad = typedNum !== null && (!Number.isInteger(typedNum) || typedNum < MIN || typedNum > MAX);
  const finalCount = typedNum !== null && !typedBad ? typedNum : count;
  const canStart = picked.length > 0 && !typedBad && available > 0 && !busy;

  async function start() {
    if (!canStart) return;
    setBusy(true);
    setError('');
    try {
      const res = await startPractice(picked, finalCount, pyqOnly);
      track('test_start', { test_id: res.test_id, details: { mode: 'v2_practice', count: finalCount, chapter_ids: picked, pyq_only: pyqOnly } });
      navigate(`/app/test/${res.test_id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div data-testid="v2-practice" style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 28, fontWeight: 600 }}>Practice Test</h1>
        <div style={{ fontSize: 15, color: C.muted }}>Choose chapters and how many questions. You see the answers at the end.</div>
      </div>

      <OpenTestBanner test={openTest} note="If you start a new test, this test is finished first." />

      <div style={box}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <h2 style={h2}>1. Choose chapters</h2>
          {usable.length > 1 && (
            <button type="button" data-testid="v2-pick-all" onClick={() => setPicked(allPicked ? [] : usable.map((c) => c.chapter_id))}
              style={{ ...button.secondary, minHeight: 44, fontSize: 14 }}>
              {allPicked ? 'Clear all' : 'Choose all'}
            </button>
          )}
        </div>
        {chaptersQ.isLoading && <Loading text="Loading chapters…" />}
        {chaptersQ.isError && <LoadError onRetry={chaptersQ.refetch} />}
        {!chaptersQ.isLoading && !chaptersQ.isError && chapters.length === 0 && (
          <Empty text="No chapters are open yet." />
        )}
        {chapters.length > 0 && (
          <PyqSwitch on={pyqOnly} onClick={switchPyq} count={picked.length ? pyqPicked : pyqAll} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {chapters.map((ch) => (
            <CheckRow key={ch.chapter_id} checked={picked.includes(ch.chapter_id)}
              disabled={!countOf(ch)} onClick={() => toggle(ch.chapter_id)} testId={`v2-pick-ch-${ch.chapter_id}`}>
              <span style={{ flexGrow: 1 }}>{chapterName(ch)}</span>
              <span data-testid="v2-pick-count" style={{ fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
                {pyqOnly ? plural(ch.pyqs || 0, 'PYQ', 'PYQs') : plural(ch.questions, 'question', 'questions')}
              </span>
            </CheckRow>
          ))}
        </div>
      </div>

      <div style={box}>
        <h2 style={h2}>2. How many questions?</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {QUICK.map((n) => {
            const on = typed === '' && count === n;
            return (
              <button key={n} type="button" data-testid={`v2-count-${n}`} aria-pressed={on}
                onClick={() => { setCount(n); setTyped(''); }}
                style={{
                  minWidth: 64, minHeight: 46, borderRadius: 10, fontFamily: FONT.ui, fontSize: 16,
                  fontWeight: 600, cursor: 'pointer', border: `2px solid ${on ? C.primary : C.border}`,
                  background: on ? C.tint : C.surface, color: on ? C.primaryDark : C.text,
                }}>{n}</button>
            );
          })}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: C.muted }}>
            or type
            <input type="number" inputMode="numeric" min={MIN} max={MAX} value={typed}
              data-testid="v2-count-box" placeholder="e.g. 40"
              onChange={(e) => setTyped(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
              style={{
                width: 96, height: 46, boxSizing: 'border-box', padding: '0 12px', borderRadius: 10,
                border: `2px solid ${typedBad ? C.bad : C.border}`, fontFamily: FONT.ui, fontSize: 16,
              }} />
          </label>
        </div>
        {typedBad && <div style={{ fontSize: 14, color: C.bad }}>Type a number from {MIN} to {MAX}.</div>}
        {picked.length > 0 && !typedBad && (
          <div data-testid="v2-practice-summary" style={{ fontSize: 14, color: C.muted }}>
            {finalCount > available
              ? `These chapters have ${plural(available, pyqOnly ? 'PYQ' : 'question', pyqOnly ? 'PYQs' : 'questions')}, so your test will have ${available}.`
              : `Your test will have ${finalCount} ${pyqOnly ? 'PYQs' : 'questions'}.`}
          </div>
        )}
      </div>

      {error && <div data-testid="v2-start-error" style={{ color: C.bad, fontSize: 15 }}>{error}</div>}
      <div>
        <button type="button" data-testid="v2-start" onClick={start} disabled={!canStart}
          style={{ ...button.primary, minHeight: 50, padding: '0 32px', opacity: canStart ? 1 : 0.5, cursor: canStart ? 'pointer' : 'default' }}>
          {busy ? 'Starting…' : 'Start'}
        </button>
        {picked.length === 0 && <span style={{ marginLeft: 12, fontSize: 14, color: C.muted }}>Choose at least one chapter.</span>}
      </div>
    </div>
  );
}
